import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Tabs, Tab, Autocomplete,
  Switch, Tooltip, useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  LinkOff as DetachIcon,
  Tune as ManageIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { can } from '../../utils/roleUtils';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

// Same type scale as the other Organizations tabs: 12px headers, 13px cells.
const HEAD_SX = { fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' };
const CELL_SX = { fontSize: '0.8125rem', py: 0.75 };
const EMPTY_SX = { fontSize: '0.8125rem', color: 'text.secondary', textAlign: 'center', py: 4 };
const CHIP_SX = { fontSize: '0.7rem', height: 20 };

const emptyGroupForm = { name: '', description: '' };
const emptyUserForm = { username: '', email: '', fullName: '', phone: '', password: '' };

const PortalCustomerGroups = ({ currentUser, embedded = false }) => {
  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
  const canWrite = can(currentUser, 'customer_organization', 'write');
  const theme = useTheme();
  const headSx = getBrandedTableHeadSx(theme);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('customers');

  const [groupDialog, setGroupDialog] = useState(null);
  const [groupForm, setGroupForm] = useState(emptyGroupForm);
  const [userDialog, setUserDialog] = useState(false);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [saving, setSaving] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [groupUsers, setGroupUsers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [toAttach, setToAttach] = useState([]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await ApiService.getPortalCustomerGroups({ page: 0, size: 100 });
      setGroups(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load customer groups.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadDetail = useCallback(async (group) => {
    setDetailLoading(true);
    setError('');
    try {
      const [custRes, userRes] = await Promise.all([
        ApiService.getPortalCustomerGroupCustomers(group.id, { page: 0, size: 100 }),
        ApiService.getPortalCustomerGroupUsers(group.id, { page: 0, size: 100 })
      ]);
      setCustomers(custRes.data?.data || []);
      setGroupUsers(userRes.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load group details.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadEligible = useCallback(async () => {
    if (!typingCenterId) return;
    try {
      const res = await ApiService.getInternalOrganizationsPage({ typingCenterId, page: 0, size: 200 });
      const content = res?.data?.data?.content || [];
      setEligible(content.map((o) => ({
        id: o.id,
        name: o.name || o.organization_name || o.id,
        groupId: o.group_id ?? o.groupId ?? null
      })));
    } catch (e) {
      setEligible([]);
    }
  }, [typingCenterId]);

  const openGroup = async (group) => {
    setSelected(group);
    setTab('customers');
    await Promise.all([loadDetail(group), canWrite ? loadEligible() : Promise.resolve()]);
  };

  const submitGroup = async () => {
    setSaving(true);
    setError('');
    try {
      if (groupDialog === 'create') {
        // The code is generated by the system (<TC code>-G001).
        await ApiService.createPortalCustomerGroup({ name: groupForm.name.trim(), description: groupForm.description });
      } else {
        await ApiService.updatePortalCustomerGroup(groupDialog.id, { name: groupForm.name.trim(), description: groupForm.description });
      }
      setGroupDialog(null);
      setGroupForm(emptyGroupForm);
      await loadGroups();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Could not save the group.');
    } finally {
      setSaving(false);
    }
  };

  const toggleGroupActive = async (group, active) => {
    setError('');
    try {
      await ApiService.updatePortalCustomerGroup(group.id, { active });
      await loadGroups();
      if (selected?.id === group.id) setSelected({ ...selected, active });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update the group.');
    }
  };

  const inThisGroup = (o) => !!o.groupId && !!selected && o.groupId === selected.id;
  const inAnotherGroup = (o) => !!o.groupId && !!selected && o.groupId !== selected.id;

  const attach = async () => {
    if (!toAttach.length) return;
    setError('');
    try {
      await ApiService.attachPortalCustomerGroupCustomers(selected.id, toAttach.map((o) => o.id));
      setToAttach([]);
      await Promise.all([loadDetail(selected), loadGroups(), loadEligible()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not attach the selected customers.');
    }
  };

  const detach = async (orgId) => {
    setError('');
    try {
      await ApiService.detachPortalCustomerGroupCustomers(selected.id, [orgId]);
      await Promise.all([loadDetail(selected), loadGroups(), loadEligible()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not detach the customer.');
    }
  };

  const submitUser = async () => {
    setSaving(true);
    setError('');
    try {
      await ApiService.createPortalCustomerGroupUser(selected.id, userForm);
      setUserDialog(false);
      setUserForm(emptyUserForm);
      await loadDetail(selected);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the group user.');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserActive = async (user, active) => {
    setError('');
    try {
      await ApiService.setPortalCustomerGroupUserActive(selected.id, user.id, active);
      await loadDetail(selected);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update the group user.');
    }
  };

  const newGroupButton = canWrite && (
    <Button variant="contained" size="small" startIcon={<AddIcon />}
      onClick={() => { setGroupForm(emptyGroupForm); setGroupDialog('create'); }}
      sx={{ textTransform: 'none', fontSize: '0.8125rem', height: 32, whiteSpace: 'nowrap' }}>
      New Group
    </Button>
  );

  const renderList = () => (
    <>
      {/* Toolbar — the tab already names the page when embedded in Organizations */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {embedded ? (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flexGrow: 1 }}>
            Let one portal login access several clients.
            {!loading && groups.length > 0 && ` · ${groups.length} group${groups.length === 1 ? '' : 's'}`}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, flexGrow: 1 }}>
            Portal Customer Groups
          </Typography>
        )}
        {newGroupButton}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : groups.length === 0 ? (
        <Paper variant="outlined">
          <Typography sx={EMPTY_SX}>
            No customer groups yet.{canWrite ? ' Click "New Group" to create one.' : ''}
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead sx={headSx}>
                <TableRow>
                  <TableCell sx={HEAD_SX}>Name</TableCell>
                  <TableCell sx={HEAD_SX}>Code</TableCell>
                  <TableCell align="right" sx={HEAD_SX}>Customers</TableCell>
                  <TableCell sx={HEAD_SX}>Status</TableCell>
                  <TableCell align="right" sx={HEAD_SX}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => (
                  <TableRow key={g.id} hover sx={{ cursor: 'pointer' }} onClick={() => openGroup(g)}>
                    <TableCell sx={CELL_SX}>
                      <Typography sx={{ fontSize: '0.8125rem', color: 'primary.main', fontWeight: 500 }}>{g.name}</Typography>
                      {g.description && (
                        <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 360 }}>{g.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={CELL_SX}>{g.code}</TableCell>
                    <TableCell align="right" sx={CELL_SX}>{g.customerCount ?? 0}</TableCell>
                    <TableCell sx={CELL_SX}>
                      <Chip label={g.active ? 'Active' : 'Inactive'} size="small"
                        color={g.active ? 'success' : 'default'} sx={CHIP_SX} />
                    </TableCell>
                    <TableCell align="right" sx={{ ...CELL_SX, py: 0.25, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        startIcon={<ManageIcon sx={{ fontSize: 16 }} />}
                        onClick={() => openGroup(g)}
                        sx={{ textTransform: 'none', fontSize: '0.8125rem', mr: 0.5 }}
                      >
                        Manage
                      </Button>
                      {canWrite && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" sx={{ color: 'text.secondary' }}
                              onClick={() => { setGroupForm({ name: g.name, description: g.description || '' }); setGroupDialog(g); }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={g.active ? 'Deactivate' : 'Activate'}>
                            <Switch size="small" checked={!!g.active} onChange={(e) => toggleGroupActive(g, e.target.checked)} />
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );

  const renderDetail = () => (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={() => setSelected(null)}><ArrowBackIcon fontSize="small" /></IconButton>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, flexGrow: 1 }}>
          {selected.name} <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>({selected.code})</Typography>
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, minHeight: 36, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="customers" label="Customers" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 36 }} />
        <Tab value="users" label="Group Users" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 36 }} />
      </Tabs>

      {detailLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tab === 'customers' ? (
        <>
          {canWrite && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                multiple
                size="small"
                sx={{ flexGrow: 1 }}
                options={eligible}
                value={toAttach}
                onChange={(e, v) => setToAttach(v)}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                getOptionDisabled={(o) => inThisGroup(o) || inAnotherGroup(o)}
                renderOption={({ key, ...props }, option) => (
                  <Box component="li" key={option.id} {...props} sx={{ display: 'block !important' }}>
                    <Typography variant="body2">{option.name}</Typography>
                    {(inThisGroup(option) || inAnotherGroup(option)) && (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        {inThisGroup(option)
                          ? 'Already in this group'
                          : 'Assigned to another group — detach it there first, then assign here'}
                      </Typography>
                    )}
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Add customers" placeholder="Search..." sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }} />}
              />
              <Button variant="contained" size="small" disabled={!toAttach.length} onClick={attach} sx={{ textTransform: 'none' }}>
                Attach
              </Button>
            </Box>
          )}

          {customers.length === 0 ? (
            <Typography sx={EMPTY_SX}>
              No customers in this group yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={headSx}>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>Customer</TableCell>
                    <TableCell sx={HEAD_SX}>TRN</TableCell>
                    <TableCell sx={HEAD_SX}>Portal</TableCell>
                    {canWrite && <TableCell align="right" sx={HEAD_SX}>Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 500 }}>{c.name}</TableCell>
                      <TableCell sx={CELL_SX}>{c.trn_number || c.trnNumber || '-'}</TableCell>
                      <TableCell sx={CELL_SX}>
                        <Chip label={(c.portal_enabled ?? c.portalEnabled) ? 'Enabled' : 'Disabled'} size="small"
                          color={(c.portal_enabled ?? c.portalEnabled) ? 'success' : 'warning'} variant="outlined"
                          sx={CHIP_SX} />
                      </TableCell>
                      {canWrite && (
                        <TableCell align="right">
                          <Tooltip title="Detach">
                            <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={() => detach(c.id)}><DetachIcon sx={{ fontSize: 16 }} /></IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <>
          {canWrite && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setUserForm(emptyUserForm); setUserDialog(true); }} sx={{ textTransform: 'none' }}>
                New Group User
              </Button>
            </Box>
          )}
          {groupUsers.length === 0 ? (
            <Typography sx={EMPTY_SX}>
              No group users yet. Create one so the customer group can sign in on the portal.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={headSx}>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>Username</TableCell>
                    <TableCell sx={HEAD_SX}>Name</TableCell>
                    <TableCell sx={HEAD_SX}>Email</TableCell>
                    <TableCell sx={HEAD_SX}>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupUsers.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ ...CELL_SX, fontWeight: 500 }}>{u.username}</TableCell>
                      <TableCell sx={CELL_SX}>{u.fullName || u.full_name}</TableCell>
                      <TableCell sx={CELL_SX}>{u.email}</TableCell>
                      <TableCell sx={CELL_SX}>
                        {canWrite ? (
                          <Switch size="small" checked={!!u.active} onChange={(e) => toggleUserActive(u, e.target.checked)} />
                        ) : (
                          <Chip label={u.active ? 'Active' : 'Inactive'} size="small"
                            color={u.active ? 'success' : 'default'} sx={CHIP_SX} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {selected ? renderDetail() : renderList()}

      <Dialog open={!!groupDialog} onClose={saving ? undefined : () => setGroupDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {groupDialog === 'create' ? 'New Customer Group' : 'Edit Customer Group'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" label="Group name" required autoFocus
            placeholder="e.g. Corporate clients"
            InputProps={{ sx: { fontSize: '0.8125rem' } }} InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
            value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
          <TextField fullWidth size="small" label="Description" multiline minRows={2} sx={{ mt: 2 }}
            placeholder="Who belongs in this group (optional)"
            InputProps={{ sx: { fontSize: '0.8125rem' } }} InputLabelProps={{ sx: { fontSize: '0.8125rem' } }}
            value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} />
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
            {groupDialog && groupDialog !== 'create'
              ? `Group code ${groupDialog.code}`
              : 'A group code is assigned automatically.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setGroupDialog(null)} disabled={saving} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>Cancel</Button>
          <Button variant="contained" disabled={saving || !groupForm.name.trim()} onClick={submitGroup}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
            {saving ? 'Saving…' : groupDialog === 'create' ? 'Create Group' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userDialog} onClose={() => setUserDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>New Portal Group User</DialogTitle>
        <DialogContent>
          {['username', 'email', 'fullName', 'phone', 'password'].map((field, i) => (
            <TextField key={field} fullWidth size="small" sx={{ mt: i === 0 ? 1 : 2 }}
              label={field === 'fullName' ? 'Full name' : field.charAt(0).toUpperCase() + field.slice(1)}
              type={field === 'password' ? 'password' : 'text'}
              value={userForm[field]}
              onChange={(e) => setUserForm({ ...userForm, [field]: e.target.value })} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" sx={{ textTransform: 'none' }}
            disabled={saving || !userForm.username || !userForm.email || !userForm.fullName || !userForm.password}
            onClick={submitUser}>
            {saving ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalCustomerGroups;
